import { useEffect, useState } from "react";
import { api, formatIDR, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, AlertOctagon, Package, Download, Printer } from "lucide-react";
import { toast } from "sonner";

export default function CostReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/cost").then((r) => setData(r.data));
  }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("tms_token");
      const res = await fetch(`${API}/exports/cost-report.xlsx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "laporan-biaya-ban.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal export Excel");
    }
  };

  if (!data) return <div className="text-slate-500">Memuat...</div>;

  const monthly = data.monthly.map((m) => ({ ...m, label: m.month }));
  const totalCost = data.monthly.reduce((a, b) => a + b.total, 0);

  return (
    <div className="print-area">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Laporan Biaya Ban</h1>
          <p className="text-slate-500 text-sm mt-1">Analisis biaya pembelian ban dan kerugian. Khusus Management.</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            onClick={handleExport}
            variant="outline"
            data-testid="export-cost-xlsx-btn"
            className="h-12 rounded-sm border-2 border-slate-900 font-bold uppercase tracking-wider"
          >
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button
            onClick={() => window.print()}
            data-testid="print-report-btn"
            className="h-12 rounded-sm bg-slate-900 hover:bg-slate-800 text-orange-400 font-bold uppercase tracking-wider btn-industrial"
          >
            <Printer className="w-4 h-4 mr-1" /> Cetak PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border-2 border-slate-900 bg-slate-900 text-white p-5 rounded-sm" data-testid="report-total-cost">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">Total Biaya Ban</div>
              <div className="font-display text-3xl font-black mt-2">{formatIDR(totalCost)}</div>
              <div className="text-xs text-slate-400 mt-1">Semua periode</div>
            </div>
            <TrendingUp className="w-6 h-6 text-orange-400" />
          </div>
        </div>
        <div className="border-2 border-slate-900 bg-white p-5 rounded-sm" data-testid="report-loss">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Kerugian (Ban Rusak/Hilang)</div>
              <div className="font-display text-3xl font-black text-red-700 mt-2">{formatIDR(data.loss.total)}</div>
              <div className="text-xs text-slate-500 mt-1">{data.loss.count} ban</div>
            </div>
            <AlertOctagon className="w-6 h-6 text-red-600" />
          </div>
        </div>
        <div className="border-2 border-slate-900 bg-white p-5 rounded-sm" data-testid="report-brands-count">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Jumlah Merk</div>
              <div className="font-display text-3xl font-black text-slate-900 mt-2">{data.by_brand.length}</div>
              <div className="text-xs text-slate-500 mt-1">Merk berbeda terdaftar</div>
            </div>
            <Package className="w-6 h-6 text-slate-900" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border-2 border-slate-200 bg-white p-5 rounded-sm">
          <div className="font-display font-black text-lg text-slate-900 mb-3">Biaya Pembelian per Bulan</div>
          <div className="h-64">
            {monthly.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400">Belum ada data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#64748b" style={{ fontSize: 12 }} />
                  <YAxis stroke="#64748b" style={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}jt`} />
                  <Tooltip formatter={(v) => formatIDR(v)} />
                  <Bar dataKey="total" fill="#ea580c" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border-2 border-slate-200 bg-white p-5 rounded-sm">
          <div className="font-display font-black text-lg text-slate-900 mb-3">Biaya per Merk</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest font-bold">Merk</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.by_brand.map((b) => (
                <TableRow key={b.brand} data-testid={`brand-row-${b.brand}`}>
                  <TableCell className="font-bold">{b.brand}<span className="text-slate-500 font-normal text-xs ml-1">({b.count})</span></TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(b.total)}</TableCell>
                </TableRow>
              ))}
              {data.by_brand.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className="text-center py-6 text-slate-400">Belum ada data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
